import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { templatesApi } from "@/lib/api";

// Phase 1.6 — "Save current plan as template" dialog.
// Captures: name, description, anchor_label (human friendly), optional
// anchor_date override (default: earliest prod task or earliest task start),
// include_prod toggle (default off — prod blocks come from call sheets).
export default function SaveAsTemplateDialog({
  open,
  onOpenChange,
  projectId,
  defaultAnchorDate,
  onSaved,
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [anchorLabel, setAnchorLabel] = useState("Production Day 1");
  const [anchorDate, setAnchorDate] = useState(defaultAnchorDate || "");
  const [includeProd, setIncludeProd] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName("");
    setDescription("");
    setAnchorLabel("Production Day 1");
    setAnchorDate(defaultAnchorDate || "");
    setIncludeProd(false);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Template name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        anchor_label: anchorLabel.trim() || "Production Day 1",
        include_prod: includeProd,
      };
      if (anchorDate) payload.anchor_date = anchorDate;
      const tpl = await templatesApi.snapshotFromProject(projectId, payload);
      toast.success(`Template saved — ${tpl.tasks?.length || 0} tasks captured`);
      onSaved?.(tpl);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-[520px] max-h-[90vh] overflow-y-auto" data-testid="save-template-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Save plan as template</DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Snapshots all current pre/post tasks and dependencies. Each
            task&apos;s start date is converted to a signed offset from Day 0
            (anchor) so the template can be re-applied to any future project.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500" htmlFor="tpl-name">Name</Label>
            <Input
              id="tpl-name"
              data-testid="tpl-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Commercial — Mid-Size Brand"
              className="bg-zinc-950 border-zinc-800"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500" htmlFor="tpl-desc">Description</Label>
            <Textarea
              id="tpl-desc"
              data-testid="tpl-desc-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="When to use this template, who it's for…"
              className="bg-zinc-950 border-zinc-800 min-h-[80px]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500" htmlFor="tpl-anchor-label">Anchor label</Label>
              <Input
                id="tpl-anchor-label"
                data-testid="tpl-anchor-label-input"
                value={anchorLabel}
                onChange={(e) => setAnchorLabel(e.target.value)}
                placeholder="Production Day 1"
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500" htmlFor="tpl-anchor-date">Anchor date</Label>
              <Input
                id="tpl-anchor-date"
                data-testid="tpl-anchor-date-input"
                type="date"
                value={anchorDate}
                onChange={(e) => setAnchorDate(e.target.value)}
                className="bg-zinc-950 border-zinc-800 font-mono"
              />
              <p className="text-[10px] text-zinc-600">Defaults to earliest production task</p>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-400">
            <input
              type="checkbox"
              data-testid="tpl-include-prod-checkbox"
              checked={includeProd}
              onChange={(e) => setIncludeProd(e.target.checked)}
              className="w-3.5 h-3.5 accent-amber-500"
            />
            Include production shoot days
            <span className="text-[10px] text-zinc-600 ml-auto">(usually off — comes from call sheets)</span>
          </label>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-zinc-400"
            data-testid="tpl-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="bg-amber-500 hover:bg-amber-400 text-black"
            data-testid="tpl-save-btn"
          >
            {saving && <Loader2 className="w-3 h-3 mr-2 animate-spin" />}
            Save template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
